import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { CheckCircle2, Loader2, X } from "lucide-react";

const CertificateRequestBulkApproveModal = ({
  open,
  mode = "hq",
  count = 0,
  studentCount = 0,
  teacherCount = 0,
  templateOptions = [],
  busy = false,
  onClose,
  onConfirm,
}) => {
  const [templateId, setTemplateId] = useState("");

  useEffect(() => {
    if (!open) return;
    setTemplateId(templateOptions[0]?.id || "");
  }, [open, templateOptions]);

  if (!open) return null;

  const isHq = mode === "hq";
  const needsTemplate = isHq && studentCount > 0;
  const canConfirm = !busy && (!needsTemplate || templateId);

  const modalContent = (
    <AnimatePresence>
      <div className="fixed inset-0 z-[280] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => !busy && onClose()}
        />
        {busy ? (
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative z-[290] rounded-2xl bg-white px-6 py-5 shadow-xl flex items-center gap-3"
          >
            <Loader2 className="animate-spin text-red-600 shrink-0" size={22} />
            <p className="text-sm font-bold text-gray-700">Đang duyệt hàng loạt, vui lòng chờ…</p>
          </motion.div>
        ) : (
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative z-[290] w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        >
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h3 className="font-black text-gray-800">
                {isHq ? "Duyệt hàng loạt cấp 1" : "Duyệt hàng loạt cấp 2"}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {isHq
                  ? "Chuyển các yêu cầu đã chọn sang bước chờ duyệt lãnh đạo."
                  : "Phê duyệt và sinh giấy chứng nhận cho các yêu cầu đã chọn."}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 disabled:opacity-50"
            >
              <X size={18} />
            </button>
          </div>

          <div className="mb-4 rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-sm text-gray-700">
            <p>
              Số yêu cầu: <strong className="text-red-700">{count}</strong>
            </p>
            {isHq && (
              <p className="mt-1 text-xs text-gray-500">
                Gồm {studentCount} yêu cầu học viên
                {teacherCount > 0 ? ` và ${teacherCount} yêu cầu giáo viên` : ""}.
              </p>
            )}
            {!isHq && (
              <p className="mt-1 text-xs text-amber-700">
                Thao tác này có thể mất vài phút nếu mỗi yêu cầu có nhiều hồ sơ.
              </p>
            )}
          </div>

          {needsTemplate && (
            <div className="mb-4">
              <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-400">
                Biểu mẫu giấy chứng nhận (học viên) <span className="text-red-600">*</span>
              </label>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                disabled={busy}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-60"
              >
                <option value="">Chọn biểu mẫu</option>
                {templateOptions.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </option>
                ))}
              </select>
              {templateOptions.length === 0 && (
                <p className="mt-2 text-xs font-bold text-amber-700">
                  Chưa có biểu mẫu học viên. Vui lòng tạo biểu mẫu trước khi duyệt.
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-600 disabled:opacity-50"
            >
              Hủy
            </button>
            <button
              type="button"
              disabled={!canConfirm}
              onClick={() => onConfirm({ templateId: needsTemplate ? templateId : undefined })}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              {isHq ? "Duyệt cấp 1" : "Duyệt cấp 2"}
            </button>
          </div>
        </motion.div>
        )}
      </div>
    </AnimatePresence>
  );

  if (typeof document !== "undefined") {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default CertificateRequestBulkApproveModal;
